import { Role } from '@ypp-portal/models/user/role';
import { UserData } from '@ypp-portal/models/user/userData';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import { useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useDocument } from 'react-firebase-hooks/firestore';
import { useAsync } from './useAsync';
import { IdTokenWithClaims } from '@src/authContexts';
import { InstructorDataConverter } from '@data/user/instructorDataConverter';

const instructorConverter = new InstructorDataConverter();

const mapRoleToCollection = (role: Role | null | undefined) => {
  if (!role) {
    return null;
  }

  const collectionNameMap = {
    [Role.Admin]: 'admins',
    [Role.Instructor]: 'instructors',
    [Role.Parent]: 'parents',
    [Role.Applicant]: 'applicants',
  };
  const collectionName = collectionNameMap[role];

  const converterMap = {
    [Role.Admin]: null,
    [Role.Instructor]: instructorConverter,
    [Role.Parent]: null,
    [Role.Applicant]: null,
  };
  const converter = converterMap[role];

  if (converter) {
    return firebase.firestore().collection(collectionName).withConverter(converter);
  }

  return firebase.firestore().collection(collectionName);
};

export const useUserData = (): [
  firebase.User | null | undefined,
  IdTokenWithClaims | null | undefined,
  firebase.firestore.DocumentSnapshot<UserData> | null | undefined,
  boolean,
  firebase.auth.Error | Error | undefined
] => {
  const [user, userLoading, userError] = useAuthState(firebase.auth());

  const getIdToken = useCallback(async () => user?.getIdTokenResult(), [user]);
  const [token, tokenLoading, tokenError] = useAsync(getIdToken);

  const collection = mapRoleToCollection(token?.claims.role);

  const query = collection?.doc(user?.uid);

  const [doc, docLoading, docError] = useDocument<UserData>(query);

  return [
    user,
    token as IdTokenWithClaims,
    doc,
    userLoading || tokenLoading || docLoading,
    userError ?? tokenError ?? docError,
  ];
};
